/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useRef, useState } from 'react';
import { Sparkles, TrendingDown, TrendingUp, Zap, Check, Maximize2, X, Expand, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import AiScreeningPanel from './AiScreeningPanel';

const API = process.env.REACT_APP_BACKEND_URL;

/* ─── Scroll reveal ───────────────────────────────────────────── */
function useReveal() {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    if (!ref.current || seen) return;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold: 0.15 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [seen]);
  return [ref, seen];
}
const Reveal = ({ children, delay = 0, className = '' }) => {
  const [ref, seen] = useReveal();
  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${seen ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'} ${className}`}
      style={{ transitionDelay: seen ? `${delay}ms` : '0ms' }}
    >
      {children}
    </div>
  );
};

/* ─── KPIs ─────────────────────────────────────────────────────── */
const HERO_KPIS = [
  { big: '9 / 10',   small: 'divisions claimed',             sub: '9 of 10 MP divisions submitted a claim · every submission was AI-cleared' },
  { big: '540+',     small: 'documents AI-processed',        sub: 'proposals, quotations, invoices, KYCs, resolutions' },
  { big: '< 60s',   small: 'median AI screening',           sub: 'per grant claim · was days of paper review' },
  { big: '100%',     small: 'claims AI-cleared',             sub: 'zero human first-touch approvals' },
];

const BEFORE_AFTER = [
  { task: 'Grant claim: 5-step AI screening',   before: 'Days of paper review',     after: '< 60 sec',    pct: 99 },
  { task: 'Invoice-to-PO reconciliation',       before: '12 min per invoice',       after: '< 30 sec',    pct: 96 },
  { task: 'Bank reconciliation (monthly)',      before: '1.5–2 days',               after: '< 45 min',    pct: 96 },
  { task: 'Vendor KYC onboarding',              before: '20 min per vendor',        after: '2 min',       pct: 90 },
  { task: 'Squad selection (per tournament)',   before: '3-hour committee',         after: '5 min',       pct: 97 },
  { task: 'Executive report generation',        before: '45 min analyst work',      after: '10 sec',      pct: 99 },
  { task: 'Payment approval routing',           before: '15–20 min per approval',   after: '< 1 min',     pct: 95 },
];

// Real production data — 9 MPL Infrastructure Grants · document processing volumes.
// Docs = proposal + covering letter + N quotations + timeline + resolution (varies by # line items).
const REAL_GRANTS = [
  { div: 'Rewa',      docs: 14, flags: 6, rescreens: 2, outcome: '5 / 5 AI-cleared' },
  { div: 'Sagar',     docs: 11, flags: 4, rescreens: 2, outcome: '5 / 5 AI-cleared' },
  { div: 'Jabalpur',  docs: 13, flags: 5, rescreens: 2, outcome: '5 / 5 AI-cleared' },
  { div: 'Chambal',   docs: 10, flags: 4, rescreens: 2, outcome: '5 / 5 AI-cleared' },
  { div: 'Shahdol',   docs: 12, flags: 3, rescreens: 1, outcome: '5 / 5 AI-cleared' },
  { div: 'Indore',    docs:  9, flags: 2, rescreens: 1, outcome: '5 / 5 AI-cleared' },
  { div: 'Ujjain',    docs:  9, flags: 2, rescreens: 1, outcome: '5 / 5 AI-cleared' },
  { div: 'Bhopal',    docs: 12, flags: 4, rescreens: 2, outcome: '5 / 5 AI-cleared' },
  { div: 'Gwalior',   docs: 11, flags: 3, rescreens: 1, outcome: '5 / 5 AI-cleared' },
];

// AI throughput snapshot — volumes and velocity, no financials.
const LEAGUE_SNAPSHOT = [
  { label: 'Grant docs AI-screened',    value: '101',     of: 'across 9 division claims · 5 checks each' },
  { label: 'Invoices matched to PO',    value: '144',     of: 'of 169 POs · reconciled line-by-line by AI' },
  { label: 'Vendor KYCs auto-extracted',value: '60+',     of: 'PAN, GSTIN, MSME lifted from certificates' },
  { label: 'Avg AI time per document',  value: '~8 sec',  of: 'end-to-end OCR → verdict → coaching packet' },
];

/* ─── 7 capabilities · one-liner + synthetic AI example + live shot ─── */
const CAPABILITIES = [
  {
    n: 1,
    t: 'Smart Budget Identification',
    v: 'PO auto-tagged to budget head · live consumption bars',
    slug: 'dashboard-expense',
    example: { input: 'New PO for turf rolls', output: 'Head → Ground Maintenance · 34% consumed · Head Auto-Tagged' },
  },
  {
    n: 2,
    t: 'AI Email Hub',
    v: 'Inbox → priority-tagged open issues, in real time',
    slug: 'email-hub',
    example: { input: '47 unread emails scanned', output: '3 flagged as "payment overdue" · 2 as "vendor query" · rest archived' },
  },
  {
    n: 3,
    t: 'Grant Claim 5-step Wizard',
    v: 'Claim moves through the pipeline only after 5/5 AI checks pass',
    slug: 'grant-claims-list',
    example: { input: 'Bhopal grant · 12 docs submitted', output: '4 gaps flagged → coached → re-uploaded → 5/5 cleared · advances to Paid' },
  },
  {
    n: 4,
    t: 'Invoice-to-PO Matching',
    v: 'PDF → structured match against the originating PO',
    slug: 'invoices',
    example: { input: 'INV-2341 uploaded (PDF)', output: 'Matched to PO-089 · 12 / 12 line items reconciled · 0 variance · ready to pay' },
  },
  {
    n: 5,
    t: 'Vendor KYC Extraction',
    v: 'Drop the certificate → PAN, GSTIN, MSME auto-filled',
    slug: 'vendors',
    example: { input: 'GSTIN certificate PDF', output: 'PAN AABCM1234K · GSTIN 23AABCM1234K1Z9 · MSME UDYAM-MP-05-0001234 · state verified' },
  },
  {
    n: 6,
    t: 'Budget-upload Anomaly Detection',
    v: 'Outliers, duplicates, negatives — flagged at upload',
    slug: 'budget-upload',
    example: { input: 'Budget CSV · 89 rows', output: '3 outliers · 1 duplicate row · 0 negatives · surfaced before commit' },
  },
  {
    n: 7,
    t: 'AI Assistant Everywhere',
    v: 'Plain-English Q&A over the live ledger, sourced',
    slug: 'ai-assistant',
    example: { input: '"Which POs still await first invoice?"', output: '25 POs listed with owners, dates, and drill-down links · every answer sourced' },
  },
];

export default function Storyline() {
  const [presentation, setPresentation] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null); // index into CAPABILITIES
  const lightbox = lightboxIdx == null ? null : CAPABILITIES[lightboxIdx];
  const openLightbox = (idx) => setLightboxIdx(idx);
  const closeLightbox = () => setLightboxIdx(null);
  const stepLightbox = (dir) =>
    setLightboxIdx((i) => (i == null ? i : (i + dir + CAPABILITIES.length) % CAPABILITIES.length));

  useEffect(() => {
    document.title = 'MPL 2026 · AI-first cricket administration · A POC briefing';
  }, []);

  // Keyboard shortcuts while lightbox is open: Esc / ArrowLeft / ArrowRight
  useEffect(() => {
    if (lightboxIdx == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') closeLightbox();
      else if (e.key === 'ArrowRight') stepLightbox(1);
      else if (e.key === 'ArrowLeft') stepLightbox(-1);
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxIdx]);

  // Scale ALL rem-based Tailwind classes by lifting root <html> font-size.
  // Default 16.5px (boardroom-comfortable), Presentation 20px (projector-friendly).
  useEffect(() => {
    const root = document.documentElement;
    const prev = root.style.fontSize;
    root.style.fontSize = presentation ? '20px' : '16.5px';
    return () => { root.style.fontSize = prev; };
  }, [presentation]);

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100 selection:bg-orange-500/40 selection:text-white">
      <div
        className="pointer-events-none fixed inset-0 opacity-[0.035] mix-blend-screen z-0"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
        }}
      />

      {/* Sticky top bar */}
      <header className="fixed top-0 inset-x-0 z-40 backdrop-blur-md bg-slate-950/70 border-b border-white/5">
        <div className="max-w-[1600px] mx-auto px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/mpl-logo.png" alt="MPL" className="h-10 w-10 object-contain drop-shadow-[0_0_10px_rgba(249,115,22,0.35)]" />
            <div className="leading-tight">
              <div className="text-sm font-semibold text-white">MPL 2026 · AI-first cricket</div>
              <div className="text-xs uppercase tracking-[0.15em] text-orange-400/85">POC briefing</div>
            </div>
          </div>
          <button
            onClick={() => setPresentation((p) => !p)}
            data-testid="storyline-presentation-toggle"
            className={`inline-flex items-center gap-2 text-sm font-medium px-4 py-2 rounded-full border transition ${
              presentation
                ? 'text-orange-200 border-orange-400/50 bg-orange-500/15'
                : 'text-slate-300 hover:text-white border-white/10 hover:border-white/25'
            }`}
            title="Toggle projector-friendly font sizes"
          >
            <Maximize2 className="w-4 h-4" />
            {presentation ? 'Presentation ON' : 'Presentation'}
          </button>
        </div>
      </header>

      {/* ═══ HERO — ONE LINE + KPI GRID ═══ */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute top-1/3 -left-40 w-[700px] h-[700px] rounded-full bg-orange-500/15 blur-[140px]" />
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] rounded-full bg-emerald-700/15 blur-[130px]" />
        </div>
        <div className="relative z-10 max-w-[1600px] mx-auto px-10 pt-32 pb-12">
          <Reveal>
            <div className="flex items-center gap-4 mb-8">
              <img src="/brand/mpl-logo.png" alt="MPL" className="h-16 w-16 object-contain drop-shadow-[0_0_30px_rgba(249,115,22,0.4)]" />
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-orange-300/90 mb-1">MPCA · MPL 2026</div>
                <div className="text-sm text-slate-400 italic">Under the vision of Mr Mahaanaryaman Rao Scindia, President, MPCA</div>
              </div>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05] mb-4" data-testid="storyline-hero-title">
              A POC in <span className="italic text-orange-300">AI-first</span><br />
              cricket administration.
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-lg sm:text-xl text-slate-400 max-w-3xl mb-2">
              Live in production. Every claim. Every document. Every player of MPL 2026.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ═══ KPI GRID — THE MEAT ═══ */}
      <section className="relative z-10">
        <div className="max-w-[1600px] mx-auto px-10 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
            {HERO_KPIS.map((k, i) => (
              <Reveal key={i} delay={i * 80}>
                <div className="p-6 rounded-2xl bg-gradient-to-br from-orange-500/15 via-orange-500/5 to-transparent border border-orange-400/40 h-full">
                  <div className="text-5xl sm:text-6xl font-bold tabular-nums leading-none whitespace-nowrap bg-gradient-to-b from-white to-orange-100 bg-clip-text text-transparent">
                    {k.big}
                  </div>
                  <div className="text-sm text-orange-300 font-semibold mt-4 tracking-wide">{k.small}</div>
                  <div className="text-xs text-slate-400 mt-1.5 leading-snug">{k.sub}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <article className="relative z-10 max-w-[1600px] mx-auto px-10 pb-28">

        {/* ═══ THE FLAGSHIP OUTCOME — actual 9 divisions ═══ */}
        <Reveal>
          <div className="mt-20 mb-5">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-3">The flagship outcome</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight max-w-5xl">
              9 of 10 MP divisions submitted an infrastructure grant claim. Every one cleared.
            </h2>
          </div>
        </Reveal>
        <Reveal delay={60}>
          <p className="text-slate-400 text-lg sm:text-xl mb-8 max-w-5xl leading-relaxed">
            <strong className="text-white">9 division claims</strong> · <strong className="text-white">101 documents</strong> AI-screened end-to-end · <strong className="text-white">33 gaps</strong> caught and coached before a human reviewer opened the claim. Every claim eventually cleared the 5-step AI gauntlet.
          </p>
          <div className="overflow-hidden rounded-xl border border-white/10 my-6">
            <table className="w-full text-base">
              <thead className="bg-orange-950/40 text-orange-300 text-xs uppercase tracking-wider">
                <tr>
                  <th className="px-5 py-3.5 text-left font-semibold">Division</th>
                  <th className="px-5 py-3.5 text-right font-semibold">Docs AI-screened</th>
                  <th className="px-5 py-3.5 text-right font-semibold">Gaps caught</th>
                  <th className="px-5 py-3.5 text-right font-semibold">Re-screens</th>
                  <th className="px-5 py-3.5 text-right font-semibold">Final AI verdict</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {REAL_GRANTS.map((g, i) => (
                  <tr key={i} className="hover:bg-white/[0.02] transition">
                    <td className="px-5 py-3 text-white font-medium">{g.div}</td>
                    <td className="px-5 py-3 text-right text-slate-300 tabular-nums">{g.docs}</td>
                    <td className="px-5 py-3 text-right text-amber-300 font-semibold tabular-nums">{g.flags}</td>
                    <td className="px-5 py-3 text-right text-slate-400 tabular-nums">{g.rescreens}</td>
                    <td className="px-5 py-3 text-right">
                      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-emerald-300 bg-emerald-500/10 border border-emerald-400/30 rounded px-2 py-1">
                        <Check className="w-3 h-3" strokeWidth={3} /> {g.outcome}
                      </span>
                    </td>
                  </tr>
                ))}
                <tr className="bg-orange-500/10 font-bold text-base">
                  <td className="px-5 py-3.5 text-white">TOTAL · 9 divisions</td>
                  <td className="px-5 py-3.5 text-right text-slate-100 tabular-nums">101</td>
                  <td className="px-5 py-3.5 text-right text-amber-300 tabular-nums">33</td>
                  <td className="px-5 py-3.5 text-right text-slate-100 tabular-nums">14</td>
                  <td className="px-5 py-3.5 text-right text-emerald-300">100% AI-cleared</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center gap-3 p-4 rounded-xl border border-amber-400/25 bg-amber-500/[0.06]">
            <AlertTriangle className="w-5 h-5 text-amber-300 flex-shrink-0" />
            <div className="text-sm sm:text-base text-slate-200 leading-snug">
              <span className="font-semibold text-white">Narmadapuram</span> — the 10th MP division — did not submit any documents this season, so no AI screening ran for them. Every other division claimed and cleared.
            </div>
          </div>
        </Reveal>

        {/* ═══ AI SCREENING · SHOW THE ACTUAL AI OUTPUTS ═══ */}
        <Reveal>
          <div className="mt-24 mb-5">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-3">See it screen · in real time</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight max-w-5xl">
              This is what the AI actually said. Every step. Every claim.
            </h2>
            <p className="text-slate-400 text-lg sm:text-xl mt-5 max-w-5xl leading-relaxed">
              The panel below auto-cycles through three real Infrastructure Grant claims — Bhopal, Sagar, and Chambal — replaying the 5-step AI screening for each with verbatim rationale and inline coaching for every gap it caught. This is what runs before a human reviewer ever opens the claim.
            </p>
          </div>
        </Reveal>
        <Reveal delay={80}>
          <AiScreeningPanel />
        </Reveal>

        {/* ═══ THROUGHPUT SNAPSHOT — real volumes running now ═══ */}
        <Reveal>
          <div className="mt-24 mb-5">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-3">AI throughput · live data</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">
              Every document read. Every line reconciled. In real time.
            </h2>
          </div>
        </Reveal>
        <Reveal delay={60}>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {LEAGUE_SNAPSHOT.map((s, i) => (
              <div key={i} className="p-6 rounded-xl bg-white/[0.02] border border-white/10">
                <div className="text-3xl sm:text-4xl font-bold text-white tabular-nums leading-none">{s.value}</div>
                <div className="text-sm text-orange-300 font-semibold mt-3">{s.label}</div>
                <div className="text-xs text-slate-500 mt-1.5 leading-snug">{s.of}</div>
              </div>
            ))}
          </div>
        </Reveal>

        {/* ═══ TIME-SAVED · THE MEAT ═══ */}
        <Reveal>
          <div className="mt-24 mb-8">
            <div className="text-xs uppercase tracking-[0.2em] text-orange-400 mb-3">Time saved · per workflow</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">Where the AI actually moved the needle.</h2>
          </div>
        </Reveal>
        <div className="grid gap-3">
          {BEFORE_AFTER.map((r, i) => (
            <Reveal key={i} delay={i * 40}>
              <div className="grid grid-cols-12 gap-4 items-center p-4 rounded-xl bg-white/[0.02] border border-white/10 hover:border-orange-400/30 transition">
                <div className="col-span-12 sm:col-span-4 text-base text-slate-100 font-medium">{r.task}</div>
                <div className="col-span-4 sm:col-span-2 flex items-center gap-2 text-sm">
                  <TrendingDown className="w-4 h-4 text-rose-400" />
                  <span className="text-slate-500 line-through">{r.before}</span>
                </div>
                <div className="col-span-4 sm:col-span-2 flex items-center gap-2 text-sm">
                  <TrendingUp className="w-4 h-4 text-emerald-400" />
                  <span className="text-emerald-300 font-semibold">{r.after}</span>
                </div>
                <div className="col-span-4 sm:col-span-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-white/5 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full bg-gradient-to-r from-orange-500 to-orange-300"
                        style={{ width: `${r.pct}%` }}
                      />
                    </div>
                    <span className="text-sm font-bold text-orange-300 tabular-nums w-14 text-right">~{r.pct}%</span>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
          <Reveal delay={BEFORE_AFTER.length * 40}>
            <div className="mt-3 flex items-center gap-3 p-4 rounded-xl bg-emerald-500/[0.06] border border-emerald-400/30">
              <Zap className="w-5 h-5 text-emerald-300 flex-shrink-0" />
              <div className="text-base text-slate-200">
                Net effect: <span className="text-white font-semibold">one full FTE-quarter of capacity</span> recovered per season — reinvested into cricket, not paperwork.
              </div>
            </div>
          </Reveal>
        </div>

        {/* ═══ THE 7 · TERSE GRID WITH LIVE AI EXAMPLES + SCREENSHOTS ═══ */}
        <Reveal>
          <div className="mt-24 mb-8">
            <div className="text-sm uppercase tracking-[0.2em] text-orange-400 mb-3">Under the hood</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-white leading-tight">Seven AI modules. One platform.</h2>
            <p className="text-lg text-slate-400 mt-4 max-w-5xl leading-relaxed">
              Each module is running in production today. Below, a synthetic example of what the AI does the moment a document, email, or CSV lands in the system — with a live production screenshot for each. <span className="text-orange-300">Click any screenshot to expand.</span>
            </p>
          </div>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-4">
          {CAPABILITIES.map((c, i) => (
            <Reveal key={c.n} delay={i * 30}>
              <div className="flex flex-col p-5 rounded-xl border border-white/10 bg-white/[0.02] hover:border-orange-400/30 hover:bg-white/[0.04] transition h-full">
                <div className="flex gap-4 items-start">
                  <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-orange-500/15 border border-orange-400/30 grid place-items-center text-orange-300 font-bold tabular-nums text-sm">
                    {String(c.n).padStart(2, '0')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-semibold text-lg mb-1">{c.t}</div>
                    <div className="text-sm text-slate-400 leading-relaxed">{c.v}</div>
                  </div>
                </div>

                {/* Live-AI mini vignette */}
                <div className="mt-4 grid gap-2">
                  <div className="rounded-lg border border-white/10 bg-slate-900/60 p-3">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-1.5 font-semibold">Input</div>
                    <div className="text-sm text-slate-200 leading-snug font-mono">{c.example.input}</div>
                  </div>
                  <div className="flex items-center gap-2 pl-3">
                    <span className="text-orange-400 text-lg leading-none">↓</span>
                    <span className="text-[10px] uppercase tracking-widest text-orange-300 font-semibold">AI</span>
                  </div>
                  <div className="rounded-lg border border-emerald-400/20 bg-emerald-500/[0.04] p-3">
                    <div className="text-[10px] uppercase tracking-widest text-emerald-300 mb-1.5 font-semibold">Output</div>
                    <div className="text-sm text-slate-100 leading-snug">{c.example.output}</div>
                  </div>
                </div>

                {/* Production screenshot thumbnail (click to expand) */}
                <button
                  type="button"
                  onClick={() => openLightbox(i)}
                  data-testid={`module-shot-${c.slug}`}
                  className="group relative mt-4 rounded-lg overflow-hidden border border-white/10 hover:border-orange-400/40 transition cursor-zoom-in bg-slate-900"
                  title={`Expand · ${c.t}`}
                >
                  <img
                    src={`${API}/api/public/case-study/pm-shots/${c.slug}.png`}
                    alt={`${c.t} · live production screenshot`}
                    loading="lazy"
                    className="w-full h-auto block group-hover:scale-[1.02] transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-slate-950/80 backdrop-blur border border-white/10 text-xs font-semibold text-white opacity-0 group-hover:opacity-100 transition-opacity">
                    <Expand className="w-3.5 h-3.5" /> Expand
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 px-3 py-2 text-[10px] uppercase tracking-wider text-slate-400 bg-slate-950/60 backdrop-blur-sm border-t border-white/5">
                    Live production · click to expand
                  </div>
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        {/* ═══ CLOSE ═══ */}
        <Reveal>
          <div className="mt-24 p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-orange-500/15 via-orange-500/5 to-transparent border border-orange-400/40">
            <div className="text-xs uppercase tracking-[0.25em] text-orange-300 mb-4 flex items-center gap-2">
              <Sparkles className="w-3.5 h-3.5" /> The POC has landed
            </div>
            <div className="text-2xl sm:text-3xl lg:text-4xl text-white font-semibold leading-snug tracking-tight mb-4">
              AI in cricket administration is no longer theoretical. It is running MPL 2026.
            </div>
            <div className="text-base sm:text-lg text-slate-300 leading-relaxed max-w-5xl">
              9 of 10 divisions claimed. 540+ documents AI-processed. 169 POs tracked live. Under the vision of <strong className="text-white">Mr Mahaanaryaman Rao Scindia</strong>, MPCA has proven a template that any of India&#39;s 37 other state cricket associations — and every national sports federation — can adopt tomorrow.
            </div>
          </div>
        </Reveal>

      </article>

      {/* Footer */}
      <footer className="relative z-10 border-t border-white/5 py-10">
        <div className="max-w-[1600px] mx-auto px-10 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/brand/mpl-logo.png" alt="MPL" className="h-10 w-10 object-contain" />
            <div>
              <div className="text-white font-semibold text-base leading-tight">Madhya Pradesh League · Scindia Cup</div>
              <div className="text-xs uppercase tracking-widest text-orange-400/80 leading-tight mt-1">MPCA · MPL 2026</div>
            </div>
          </div>
          <div className="text-xs text-slate-500 uppercase tracking-widest">A POC briefing</div>
        </div>
      </footer>

      {/* Lightbox modal for the 7 module screenshots */}
      {lightbox && (
        <div
          className="fixed inset-0 z-[60] bg-slate-950/95 backdrop-blur-md flex flex-col items-center justify-center p-6 animate-[fadeIn_0.2s_ease-out_both]"
          onClick={closeLightbox}
          role="dialog"
          aria-modal="true"
          data-testid="module-lightbox"
        >
          <style>{`@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
          <div className="w-full max-w-[1600px] flex items-center justify-between mb-4" onClick={(e) => e.stopPropagation()}>
            <div>
              <div className="text-xs uppercase tracking-[0.25em] text-orange-300 font-semibold">
                Module {String(lightbox.n).padStart(2, '0')} of {String(CAPABILITIES.length).padStart(2, '0')} · live production
              </div>
              <div className="text-2xl sm:text-3xl font-bold text-white mt-1">{lightbox.t}</div>
              <div className="text-base text-slate-400 mt-1">{lightbox.v}</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => stepLightbox(-1)}
                data-testid="module-lightbox-prev"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition"
                title="Previous module · ←"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <button
                type="button"
                onClick={() => stepLightbox(1)}
                data-testid="module-lightbox-next"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition"
                title="Next module · →"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={closeLightbox}
                data-testid="module-lightbox-close"
                className="ml-2 inline-flex items-center gap-2 px-4 py-2 rounded-full border border-white/15 bg-white/5 hover:bg-white/10 text-white text-sm font-medium transition"
                title="Close · Esc"
              >
                <X className="w-4 h-4" /> Close · Esc
              </button>
            </div>
          </div>
          <div
            className="w-full max-w-[1600px] max-h-[75vh] overflow-auto rounded-xl border border-white/10 bg-slate-900 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              key={lightbox.slug}
              src={`${API}/api/public/case-study/pm-shots/${lightbox.slug}.png`}
              alt={`${lightbox.t} · expanded live production screenshot`}
              className="w-full h-auto block"
            />
          </div>
          <div className="mt-3 text-xs text-slate-500 tracking-wide uppercase" onClick={(e) => e.stopPropagation()}>
            ← / → to browse modules · Esc to close
          </div>
        </div>
      )}
    </div>
  );
}
